import styles from './contract-details-container.module.scss';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { UseSignalRHook } from '../signalr-context/signalr-context';
import { ContactListContext } from '../customer-context/customer-context';
import { format, isAfter } from 'date-fns';
import { ContractDetails, ContractProvider, ContractStateContext, useSingleContractDispatch, ICustomerContract, createBackendOrder, ContractOrderTypesEnum, createLicenseAccessOrder, OrderTypesEnum, SnackBarContext, ILicenseAccessChangeOrder } from '@frontend-workspace/react-shared-lib';
import ContractSaleDialog from '../contract-sale-dialog/contract-sale-dialog';
import EditLicenseDialog from '../edit-license-dialog/edit-license-dialog';
import MoveContractInvoiceDialog from '../move-contract-invoice-dialog/move-contract-invoice-dialog';
import { useTranslation } from 'react-i18next';
import { SalesforceRecordLink } from '../salesforce-navigation/salesforce-navigation';

/* eslint-disable-next-line */
export interface ContractDetailsContainerProps { salesforceBaseUrl?: string }

function ContractDetailsContainerContent(props: ContractDetailsContainerProps) {
  const { companyNumber, contractNumber } = useParams();
  const companies = useContext(ContactListContext);
  const contract = useContext(ContractStateContext);
  const makeRequest = useMakeRequestHook();
  const [editLicenseNumber, setEditLicenseNumber] = useState<number | undefined>(undefined);
  const [isSaleModalOpen, setIsSaleModalOpen] = useState<boolean>(false);
  const [isMoveInvoiceOpen, setIsMoveInvoiceOpen] = useState<boolean>(false);
  const [isHandlingSale, setIsHandlingSale] = useState<boolean>(false);
  const dispatch = useSingleContractDispatch();

  const { data: reasonsForChange } = useGetRequestNewHook(`orders/reasons`, []);
  const { t } = useTranslation();


  const closeEditLicense = () => setEditLicenseNumber(undefined);
  const closeMoveInvoice = () => setIsMoveInvoiceOpen(false);
  const openMoveInvoice = () => setIsMoveInvoiceOpen(true);

  const toogleSaleModalOpen = () => setIsSaleModalOpen(!isSaleModalOpen);
  const { setSnackbarMessage } = useContext(SnackBarContext);

  const url = `contracts/${contractNumber}`;
  const getContract = () => makeRequest(url).then((contract: ICustomerContract) => {
    setIsHandlingSale(false);
    dispatch({ type: "CONTRACT_RECEIVED", contract: contract });
    getOrders();
    return contract;
  });

  const getOrders = () => makeRequest(`contracts/${contractNumber}/orders`).then(result => dispatch({ type: "ORDERS_RECEIVED", orders: result }))
  UseSignalRHook(getContract, [OrderTypesEnum.CompositeSale, OrderTypesEnum.InvoiceDateOrder, OrderTypesEnum.ChangeContractInfo]);

  useEffect(() => {
    getContract().then(result => {
      const contract: ICustomerContract = result;
      makeRequest(`contracts/actions/getSellableProducts/academic/${contract.isAcademic}/contractType/${contract.contractType}/interval/${contract.contractInfo.invoiceInterval}/currency/${contract.contractInfo.currency}`)
        .then(result => {
          dispatch({ type: "SELLABLE_PRODUCTS_RECEIVED", sellableProducts: result })
        });

        makeRequest(`companies/${companyNumber}/userAccounts/forGroup/true/onlyActive/true`).then(result => dispatch({ type: "CONTRACT_EMPLOYEE_LIST_RECEIVED", employeeList: result }));
      //Todo:: Er dette en hack? Bør listen hentes fra ett annet sted?
      {/* TODO commented out in case we want to roll back from SalesForce. Remove if this is not needed */}
      //makeRequest(`companies/${companyNumber}/action/getPersonsInSO`).then(result => dispatch({ type: "INVOICE_EMPLOYEE_LIST_RECEIVED", employeeList: result }));

    });
  }, []);

  {/* TODO commented out in case we want to roll back from SalesForce. Remove if this is not needed */}
  // useEffect(() => {
  //   const invoicedcompanyNumber = contract?.changeContractInfoOrder?.invoiceInfo.invoicedCompanyNumber || companyNumber
  //   if (contract) {
  //     makeRequest(`companies/${invoicedcompanyNumber}/action/getPersonsInSO`).then(result => dispatch({ type: "INVOICE_EMPLOYEE_LIST_RECEIVED", employeeList: result }));
  //   }
  // }, [contract?.changeContractInfoOrder?.invoiceInfo, dispatch, companyNumber]);

  const onSale = (licenseAccessChangeOrder: ILicenseAccessChangeOrder) => {
    if (contract && companyNumber) {
      const body = {
        activationDate: contract.newSaleDate || format(new Date(), "yyyy-MM-dd"),
        seatChangesOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.AddSeats, contract),
        newItemsOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.AddProductsToContract, contract),
        terminateLicenseOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.TerminateLicense, contract),
        discountPercentOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.ChangeRecurringDiscountPercent, contract),
        licenseAccessChangeOrder: createLicenseAccessOrder(companyNumber, contract, licenseAccessChangeOrder),
        companyNumber: companyNumber,
        contractNumber: contract.contractNumber,
        isSupportContract: false
      }
      setIsHandlingSale(true);
      setSnackbarMessage({ content: t("contractDetailsContainer.startingSale"), type: "info", });
      makeRequest(`contracts/action/createNewSale`, 'POST', body).then(result => {
        const isNewFutureContract = contract.newSaleDate && isAfter(contract.newSaleDate, new Date());
        toogleSaleModalOpen();
        if (isNewFutureContract && contract.newSaleDate) {
          setSnackbarMessage({ content: `${t("contractDetailsContainer.futureChangesAreImplemented")} ${format(contract.newSaleDate, "yyyy-MM-dd")}`, type: "success", });
          getContract();
          getOrders();
        }
      });
    }
  };

  const handleContractInfoChange = () => {
    if (contract?.changeContractInfoOrder && companyNumber) {
      const order = { ...contract.changeContractInfoOrder };
      order.companyNumber = companyNumber;
      makeRequest(`contracts/action/editContractDetails`, "PUT", order);
    }
  };

  const handleDeleteOrder = (id: string) => {
    makeRequest(`orders/${id}`, "DELETE").then(() => {
      setSnackbarMessage({ content: t("contractDetailsContainer.orderDeleted"), type: "success" });
      getOrders();
    }).catch(() => setSnackbarMessage({ content: t("contractDetailsContainer.couldNotDeleteOrder"), type: "error" }));
  };

  const handleHideOrder = (id: string) => {
    makeRequest(`orders/${id}/action/hide`, "PUT").then(() => {
      setSnackbarMessage({ content: t("contractDetailsContainer.orderHiddenRememberToOpenItAgainAfterChanges"), type: "success" });
      getOrders();
    }).catch(() => setSnackbarMessage({ content: t("contractDetailsContainer.couldNotHideOrder"), type: "error" }));
  }
  const handleReasonChange = (id: string, reason: string) => {
    makeRequest(`orders/${id}/action/setSaleReason`, "PUT", { orderId: id, reason: reason }).then(() => getOrders());
  };

  return <>
    <ContractDetails
      headerActions={props.salesforceBaseUrl && contract?.isLoaded && !contract.isNew &&
        contract.contractNumber === contractNumber && contract.companyNumber === companyNumber
        ? <SalesforceRecordLink baseUrl={props.salesforceBaseUrl} recordType="Contract"
            companyNumber={contract.companyNumber ?? ''} contractNumber={contract.contractNumber} /> : undefined}
      onEditLicense={setEditLicenseNumber}
      companies={companies}
      onNewSaleClick={toogleSaleModalOpen}
      onContractInfoChange={handleContractInfoChange}
      onDeleteOrder={handleDeleteOrder}
      onHideOrder={handleHideOrder}
      onOpenMoveInvoice={openMoveInvoice}
      isHandlingSale={isHandlingSale}
      reasonsForChange={reasonsForChange}
      onReasonChange={handleReasonChange}
    />
    {isSaleModalOpen && contract && companyNumber && !isHandlingSale && <ContractSaleDialog
      contract={contract}
      onClose={toogleSaleModalOpen}
      companyNumber={companyNumber}
      onNewSale={onSale}
    />}
    {Number.isInteger(editLicenseNumber) && <EditLicenseDialog
      licenseNumber={editLicenseNumber || 0}
      onClose={closeEditLicense}
    />}
    {isMoveInvoiceOpen && contract && <MoveContractInvoiceDialog
      contract={contract}
      onClose={closeMoveInvoice}
    />}
  </>;
}

export function ContractDetailsContainer(props: ContractDetailsContainerProps) {
  return <ContractProvider>
    <ContractDetailsContainerContent {...props} />
  </ContractProvider>
}

export default ContractDetailsContainer;